import { type ChangeEvent, type ReactElement } from 'react'
import { ButtonSave, FormBlocker, Page } from '@components'
import { DEFAULT_COMPETITION_NAME, usePermissionCheck, useUpdateGame, useTranslation } from '@front/model'
import { useToastError } from '@hooks'
import { useForm } from '@mantine/form'
import { hasEnded, type GameData } from '@terros/common'
import { CompetitionPageContainer } from '../competition/CompetitionPageContainer'
import { GamePermission } from '../competition_.$gameId/exports'
import { useGameHeaderContext } from '../competition_.$gameId/gameHeaderContext'
import { useGameContext } from '../competition_.$gameId/useGameContext'
import { MarketingForm } from './MarketingForm'
import { createFileRoute } from '@tanstack/react-router'

function CompetitionMarketing(): ReactElement {
  const { t } = useTranslation()
  const { data, refetch, isFetching } = useGameContext()
  const { update, isUpdating, error: updateError } = useUpdateGame()
  useToastError(updateError)
  const { data: hasPermission } = usePermissionCheck({
    permissionType: 'game:save',
    targetId: [data.game.ownerId, data.game.companyId],
  })
  const { setGame, game } = useGameHeaderContext()

  const form = useForm<GameData>({
    initialValues: toInitialValues(game),
    enhanceGetInputProps: ({ inputProps, field, form }) => ({
      ...inputProps,
      disabled: hasEnded(data.game) || !hasPermission,
      onChange: (e: ChangeEvent<HTMLInputElement> | string) => {
        if (typeof e === 'string') {
          setGame({ ...game, [field]: e })
          form.setFieldValue(field, e)
          return
        }
        if (field !== 'color') {
          setGame({ ...game, [field]: e.currentTarget.value })
          form.setFieldValue(field, e.currentTarget.value)
        }
      },
    }),
  })

  const handleSubmit = async (game: GameData): Promise<void> => {
    await update({
      ...data.game,
      name: game.name,
      tagline: game.tagline,
      description: game.description,
      color: game.color,
      terms: game.terms,
    })
    form.resetDirty(game)
  }

  return (
    <form className='w-full' onSubmit={form.onSubmit(handleSubmit)}>
      <FormBlocker form={form} />
      <CompetitionPageContainer>
        <Page.Heading isFetching={isFetching} refetch={refetch} title={t('Marketing')}>
          <GamePermission>
            <ButtonSave disabled={!form.isDirty()} loading={isUpdating} type='submit' />
          </GamePermission>
        </Page.Heading>
        <Page.Content scrollArea>
          <MarketingForm form={form} />
        </Page.Content>
      </CompetitionPageContainer>
    </form>
  )
}

const toInitialValues = (game: GameData): GameData => {
  return {
    ...game,
    name: game.name !== DEFAULT_COMPETITION_NAME ? game.name : '',
    tagline: game.tagline || '',
    description: game.description || '',
  }
}

export const Route = createFileRoute('/competition_/$gameId/marketing/')({
  component: CompetitionMarketing,
})
